<template>
  <div class="table-select-all-header">
    <BittsCheckbox
      v-if="canSelectRows"
      :checked="isAllSelected"
      :indeterminate="isSomeSelected"
      @input="handleToggleAll"
      class="mr-8"
    />
    <span>{{ params.displayName }}</span>
  </div>
</template>

<script setup lang="ts">
import { BittsCheckbox } from '@crossbeam/bitts';

import { IHeaderParams } from '@ag-grid-community/core';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const { params } = defineProps<{
  params: IHeaderParams;
}>();

const canSelectRows = computed(
  () => params.context?.canSelectRows?.() ?? false,
);

const selectedCount = ref(0);
const selectableCount = ref(0);

function updateSelectionState() {
  // Counted over the post-filter rows so the header matches what toggling it
  // does: getSelectedNodes() spans the whole grid and getDisplayedRowCount()
  // ignores isRowSelectable, so either alone lets the header drift.
  let selectable = 0;
  let selected = 0;
  params.api.forEachNodeAfterFilter((node) => {
    if (!node.selectable) return;
    selectable++;
    if (node.isSelected()) selected++;
  });
  selectableCount.value = selectable;
  selectedCount.value = selected;
}

const isAllSelected = computed(
  () =>
    selectableCount.value > 0 && selectedCount.value === selectableCount.value,
);
const isSomeSelected = computed(
  () => selectedCount.value > 0 && selectedCount.value < selectableCount.value,
);

function handleToggleAll(checked: boolean) {
  if (checked) params.api.selectAllFiltered();
  else params.api.deselectAllFiltered();
}

onMounted(() => {
  params.api.addEventListener('selectionChanged', updateSelectionState);
  params.api.addEventListener('filterChanged', updateSelectionState);
  updateSelectionState();
});

onBeforeUnmount(() => {
  if (params.api.isDestroyed()) return;
  params.api.removeEventListener('selectionChanged', updateSelectionState);
  params.api.removeEventListener('filterChanged', updateSelectionState);
});
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.table-select-all-header {
  @apply flex items-center gap-10;
}
</style>
